import { useId, type InputHTMLAttributes, type ReactNode } from 'react'
import { cx } from '../cx'
import { SectionLabel } from './SectionLabel'
import styles from './Input.module.css'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  /** Micro all-caps label above the field (spec §4 "Inputs & forms"). */
  label: string
  /** Validation or explanatory text below the field. */
  hint?: ReactNode
  /** Renders the field in an error state and announces `hint` as the message. */
  invalid?: boolean
  className?: string | undefined
}

export function Input({ label, hint, invalid = false, className, id, ...rest }: InputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const hintId = `${inputId}-hint`

  return (
    <div className={cx(styles.field, className)}>
      <label htmlFor={inputId}>
        <SectionLabel tone="muted" micro>
          {label}
        </SectionLabel>
      </label>
      <input
        id={inputId}
        className={cx(styles.input, invalid && styles.invalid)}
        aria-invalid={invalid || undefined}
        aria-describedby={hint !== undefined ? hintId : undefined}
        {...rest}
      />
      {hint !== undefined && (
        <p id={hintId} className={cx(styles.hint, invalid && styles.hintInvalid)}>
          {hint}
        </p>
      )}
    </div>
  )
}
